import {AccumulationMethodTypeType, APIStatusType, ProductOrderStatusType} from '@web/core/constants'

type SellerCustomerManagementByTitle = {[title: string]: number}

export type LogeyeRewardPoint = {
    /** 로그아이 적립포인트 */
    rewardAmount: number
    /** 맴버십 혜택여부 */
    membershipReward: boolean
}

export type LogeyeRewardPointByTitle = Record<string, LogeyeRewardPoint>

export type PurchaseRewardPointByTitle = {title: string; amount: number}

export const PurchaseRewardAdmin = {
    /** 기본 */
    DEFAULT: 'DEFAULT',
    /** 렌탈 */
    RENTAL: 'RENTAL',
    /** 정기구독 */
    SUBSCRIPTION: 'SUBSCRIPTION',
} as const

export type PurchaseRewardAdminType = Constant<typeof PurchaseRewardAdmin>

/** 기존 타입의 필드명 유지하면서 타입 수정  */
type Modify<T, R> = Pick<T, Exclude<keyof T, keyof R>> & R

/**  */
export interface PurchaseRewardPoint {
    /** 관리자 부담 적립 포인트 */
    adminAdditional: number
    /** 충전 포인트 결제 적립 포인트 (TOTAL_CHARGED_POINT) */
    chargePoint: number
    /** 충전 포인트 예상 적립 포인트 */
    expectedChargePoint: number
    /** 네이버 통장 예상 적립 포인트 */
    expectedNaverBankbook: number
    /** 멤버십 추가 적립 포인트 (TOTAL_MEMBERSHIP) */
    membership: number
    /** 판매자 추가구매적립 */
    merchant: number
    /** 네이버 통장 추가 적립 포인트 (TOTAL_CHARGED_POINT_ADDITIONAL) */
    naverBankbook: number
    /** 파워 적립 포인트 (TOTAL_ADDITIONAL) */
    power: number
    /** 기본 구매 적립 포인트 (TOTAL_PURCHASE) */
    purchase: number
    /** 도착보장 적립 포인트 (스마트스토어) */
    arrivalGuarantee?: number
    /** 도착보장 맴버십 적립 포인트 (스마트스토어) */
    arrivalGuaranteeMembership?: number
    /** 판매자 관리 추가 적립 포인트 */
    sellerCustomerManagementByTitle: SellerCustomerManagementByTitle
    /** 로그아이 적립 포인트 */
    logeyeRewardPointByTitle: LogeyeRewardPointByTitle
    /** 적립 포인트 합 (TOTAL) */
    totalRewardPoint: number
}

/** 적립명과 적립명별 amount합 interface */
export type TotalPurchaseRewardPoint = Modify<
    PurchaseRewardPoint,
    {
        /** 관리자 부담 적립 포인트 */
        adminAdditional: PurchaseRewardPointByTitle & {adminType?: PurchaseRewardAdminType}
        /** 충전 포인트 결제 적립 포인트 (TOTAL_CHARGED_POINT) */
        chargePoint: PurchaseRewardPointByTitle
        /** 멤버십 추가 적립 포인트 (TOTAL_MEMBERSHIP) */
        membership: PurchaseRewardPointByTitle
        /** 판매자 추가구매적립 */
        merchant: PurchaseRewardPointByTitle
        /** 네이버 통장 추가 적립 포인트 (TOTAL_CHARGED_POINT_ADDITIONAL) */
        naverBankbook: PurchaseRewardPointByTitle
        /** 파워 적립 포인트 (TOTAL_ADDITIONAL) */
        power: PurchaseRewardPointByTitle
        /** 기본 구매 적립 포인트 (TOTAL_PURCHASE) */
        purchase: PurchaseRewardPointByTitle
        /** 도착보장 적립 포인트 (스마트스토어) */
        arrivalGuarantee?: PurchaseRewardPointByTitle
        /** 도착보장 맴버십 적립 포인트 (스마트스토어) */
        arrivalGuaranteeMembership?: PurchaseRewardPointByTitle
        /** 정기결제 > 판매자 부담 추가적립 */
        scheduledSellerBurden?: PurchaseRewardPointByTitle
    }
>

export type ReviewRewardPoint = {
    /** 한달 사용 포토/동영상 리뷰 (TOTAL_AFTERUSE_PHOTOVIDEO) */
    afterUsePhotoVideoReview: number
    /** 한달 사용 텍스트 리뷰 (TOTAL_AFTERUSE_TEXT) */
    afterUseTextReview: number
    /** 최대 적립 포인트 (MAX_REVIEW_MILEASE) */
    maxRewardPoint: number
    /** 적립 포인트 합 (TOTAL) */
    totalRewardPoint: number
    /** 포토/동영상 리뷰 (TOTAL_NORMAL_PHOTOVIDEO) */
    writeTextReview: number
    /** 텍스트 리뷰 (TOTAL_NORMAL_TEXT) */
    writePhotoVideoReview: number
    /** 톡톡친구/스토어찜 추가 지금 포인트 */
    /** //TODO: API가 스마트스토어 대응 시점에 확인필요.(일단 optional 처리) */
    subscriberReview?: number
}

/** 적립 유형 */
export const RewardType = {
    /** 구매적립 */
    PURCHASE: 'PURCHASE',
    /** 리뷰적립 */
    REVIEW: 'REVIEW',
    /** 혜택카드적립 */
    BENEFIT_CARD: 'BENEFIT_CARD',
    /** 멤버십 예상적립 */
    EXPECTED_MEMBERSHIP: 'EXPECTED_MEMBERSHIP',
    /** 네이버예약 적립 */
    EASY_BOOKING: 'EASY_BOOKING',
    /** 이벤트 적립 */
    EVENT: 'EVENT',
} as const
export type RewardTypeType = Constant<typeof RewardType>

export const RewardTypeKeys = Object.keys(RewardType) as RewardTypeType[]

export const PurchaseSubType = {
    /** 결제대기중 상태가 아닌 경우 구매적립 유형중 멤버십 유형 존재 */
    MEMBERSHIP: 'MEMBERSHIP',
} as const

export type PurchaseSubTypeType = Constant<typeof PurchaseSubType>

export const ReviewSubType = {
    /** 상품 텍스트 적립 */
    WRITE_TEXT_REVIEW: 'WRITE_TEXT_REVIEW',
    /** 상품 포토동영상 적립 */
    WRITE_PHOTO_VIDEO_REVIEW: 'WRITE_PHOTO_VIDEO_REVIEW',
    /** 한달사용 텍스트 적립 */
    AFTER_USE_TEXT_REVIEW: 'AFTER_USE_TEXT_REVIEW',
    /** 한달사용 포토동영상 적립 */
    AFTER_USE_PHOTO_VIDEO_REVIEW: 'AFTER_USE_PHOTO_VIDEO_REVIEW',
    /** 판매자 알림받기 추가 적립 */
    SUBSCRIBER_REVIEW: 'SUBSCRIBER_REVIEW',
    // 리뷰적립 total의 경우, 세부항목들의 합산이 아닌 별도 조건을 고려하여 결정되기 때문에 직접 계산하지 않고 > 혜택 정보를 아래의 subType으로 저장하여 사용
    /** 한달사용리뷰 적립 total */
    AFTER_USE_WRITE_TOTAL: 'AFTER_USE_WRITE_TOTAL',
    /** 일반리뷰 적립 total */
    WRITE_TOTAL: 'WRITE_TOTAL',
    /** 리뷰적립 total */
    REVIEW_TOTAL: 'REVIEW_TOTAL',
    /** 상품 텍스트 이력 적립 */
    WRITTEN_WRITE_TEXT_REVIEW: 'WRITTEN_WRITE_TEXT_REVIEW',
    /** 상품 포토동영상 이력 적립 */
    WRITTEN_WRITE_PHOTO_VIDEO_REVIEW: 'WRITTEN_WRITE_PHOTO_VIDEO_REVIEW',
    /** 한달사용 텍스트 이력 적립 */
    WRITTEN_AFTER_USE_TEXT_REVIEW: 'WRITTEN_AFTER_USE_TEXT_REVIEW',
    /** 한달사용 포토동영상 이력 적립 */
    WRITTEN_AFTER_USE_PHOTO_VIDEO_REVIEW: 'WRITTEN_AFTER_USE_PHOTO_VIDEO_REVIEW',
    /** 판매자 알림받기 추가 이력 적립 */
    WRITTEN_SUBSCRIBER_REVIEW: 'WRITTEN_SUBSCRIBER_REVIEW',
} as const

export type ReviewSubTypeType = Constant<typeof ReviewSubType>

/** 적립 번들 아이디 유형 */
export const BundleIdType = {
    ITEM_ID: 'ITEM_ID',
    ORDER_NO: 'ORDER_NO',
} as const
export type BundleIdTypeType = Constant<typeof BundleIdType>

/** 적립 항목 */
export type RewardItem = {
    /** 적립유형 */
    type: RewardTypeType
    /** 적립 서브 타입 */
    subType?: PurchaseSubTypeType | ReviewSubTypeType
    /** 적립명 */
    title?: string
    /** 적립금액 */
    amount: number
    /** 번들 아이디 타입 */
    bundleIdType?: BundleIdTypeType
    /** 번들 아이디 */
    bundleId?: string
    /** 멤버십 혜택 여부 */
    hasMembershipBenefit: boolean
    /** 포인트 적립까지 걸리는 기간 */
    dateDelay?: number
}

export type RewardState = {
    /** 혜택 API별 status */
    apiStatus: {
        [key in RewardTypeType]: APIStatusType
    }
    /** 혜택카드 tid값 */
    benefitCardRewardTid: Maybe<string>
    /** 적립 내역 */
    items: RewardItem[]
    /**
     * 혜택카드 적립 포인트
     * @deprecated RewardState.items를 사용하세요.
     */
    cardEasyPayRewardPoint: number
    /**
     * 구매 적립 포인트
     * @deprecated RewardState.items를 사용하세요.
     */
    purchaseRewardPoint: {
        /** 상품 별 구매 적립 포인트 */
        pointsByOrderSheetItemId: {
            [orderSheetItemId: string]: PurchaseRewardPoint
        }
        pointsByProductOrderNo: {
            [productOrderNo: string]: PurchaseRewardPoint
        }
        /** 포인트 적립까지 걸리는 기간 */
        dateDelay?: number
        /** @deprecated total 금액은 selector에서 계산되어야 합니다. */
        total: TotalPurchaseRewardPoint
    }
    /**
     * 리뷰 적립 포인트
     * @deprecated RewardState.items를 사용하세요.
     * */
    reviewRewardPoint: {
        total: ReviewRewardPoint
        pointsByOrderSheetItemId: {
            [orderSheetItemId: string]: ReviewRewardPoint
        }
        pointsByProductOrderNo: {
            [productOrderNo: string]: ReviewRewardPoint
        }
    }
    /** 예약추가적립 */
    easyBookingRewardPoint: {
        total: number
        pointsByTitle: {
            [title: string]: {
                /** 네이버 멤버십 아이콘 여부 */
                naverMembership: boolean
                /** 적립 포인트 */
                rewardPoint: number
            }
        }
    }
    /** 충전결제 사용시 예상 적립 포인트 총액 */
    totalExpectedPurchaseRewardPoint: number

    /** 간편결제시 기본적립 포인트 */
    easyPayPurchaseRewardPoint?: number

    /** 충전결제 사용시 충전 포인트 예상 적립 포인트 */
    expectedChargePoint?: number

    /** 네이버 통장 예상 적립 포인트 */
    expectedNaverBankbook?: number

    /** 멤버십 미가입자 > 멤버십 예상 적립금 */
    expectedMembershipPointAmount: number

    /** 월간 적립 금액 */
    monthlyRewardAmount?: number

    /** 상품정보 (혜택 정보 재조회를 위한 정보 포함) */
    products: {
        // 상품주문번호
        itemId: string
        // 상품 이미지 URL
        imageUrl: Maybe<string>
        // 상품 이름
        name: string
        // 상품 카테고리 대분류
        largeCategoryId?: Maybe<string>
        // 상품 카테고리 중분류
        middleCategoryId?: Maybe<string>
        // 상품 카테고리 소분류
        smallCategoryId?: Maybe<string>
        // 서비스 타입
        serviceType?: Maybe<string>
        // 가맹점 번호
        merchantNo?: Maybe<string>
        /** 상품주문상태 */
        productOrderStatusType?: ProductOrderStatusType
        /** 네이버혜택 여부 */
        naverBenefit?: boolean
        // 주문정보
        payAmountInfo?: {
            // 상품 주문 금액
            productAmount: number
            // 총 결제 금액
            totalPayAmount: number
            // 총 포인트 사용 금액
            totalPointPayAmount: number
            // 주문 예약 채널 타입
            scheduledChannelTypeCode: Maybe<string>
            // 렌탈 주문 유형
            rentalOrderClassCode: Maybe<string>
            // 총 선할인 금액
            initOrderTotalPromotionApplyAmount: Maybe<number>
        }
        // 리뷰작성 완료 여부
        writtenReview?: boolean
        /** 옵션정보 */
        optionInfo?: {
            contents: string
            quantity: number
        }
    }[]
    /** 적립 방식 */
    accumulationMethod?: {
        type: AccumulationMethodTypeType
        /** 적립 대기 기간 */
        maxWaitingPeriod?: number
        /** 적립 일자 */
        accumulationDate?: string
        /** 적립 예정 일자 */
        expectedAccumulationDate?: string
        /** 거래 대상 */
        transactionTarget?: string
        /** 포인트 뽑기 가능 여부 */
        isPointPickEnabled?: boolean
    }
    /** 렌탈 감사 포인트 */
    rentalGiftPoint?: number
    paymentStatus?: 'expected' | 'purchaseDecided'

    /** 적립 가능한 이벤트 목록 */
    events: CombineUnion<
        {
            /** 이벤트명 */
            name: string
            /** 이벤트 상세 URL */
            url?: string
            /** 조건 결제수단 */
            conditionPaymentMethod?: string
            /** 조건 금액 */
            conditionAmount: number
            /** 최대 적립 금액 */
            maxRewardAmount: number
        },
        [
            {
                /** 적립액 */
                rewardAmount: number
            },
            {
                /** 적립률 */
                rewardRate: number
            },
        ]
    >[]
}

export type RootState = {reward: RewardState}

/** 아이템 별 로그아이 적립 포인트 */
export type LogeyeRewardPointListsByItemId = {
    /** 정책명 */
    policyName: string
    /** 로그아이 적립 포인트 */
    rewardPointAmount: number
    /** 멤버십 혜택여부 */
    membershipReward: boolean
}[]

export type OrderSheetItemId = string

/** 아이템 별 구매 적립 포인트 */
export type PurchaseRewardPointsByItemId = {
    /** 관리자 기본 적립 포인트 */
    admin: number
    /** 판매자 기본 적립 포인트 */
    seller: number
    /** 판매자 관리 추가 적립 포인트 */
    sellerCustomerManagementByTitle: Maybe<SellerCustomerManagementByTitle>
    /** 관리자 추가 적립 포인트 */
    adminAdditional: number
    /** 파워 적립 포인트 */
    power: number
    /** 충전 포인트 결제 적립 포인트 */
    chargePoint: number
    /** 네이버 통장 추가 적립 포인트 */
    naverBankbook: number
    /** 도착보장 적립 포인트 */
    arrivalGuarantee: number
    /** 도착보장 멤버십 적립 포인트 */
    arrivalGuaranteeMembership: number
    /** 맴버십 추가 적립 포인트 */
    membership: Maybe<number>
}

/** 아이템 별 리뷰 적립 포인트 */
export type ReviewRewardPointsByItemId = {
    /** 텍스트 리뷰 */
    writeTextReview: number
    /** 포토/동영상 리뷰 */
    writePhotoVideoReview: number
    /** 한달 사용 텍스트 리뷰 */
    afterUseTextReview: number
    /** 한달 사용 포토/동영상 리뷰 */
    afterUsePhotoVideoReview: number
    /** 찜하기 리뷰 */
    subscriberReview: number
    /** 최대 적립 포인트 */
    maxRewardPoint: number
}
